import React, {
  useCallback, useEffect, useState,
} from 'react';
import * as Styles from './ManageCloudFlareHandler.styles';
import {
  Button, Skeleton, IconTrashBg, IconLive, IconSecurityCheck, IconDoneChecklist,
  IconAtencion,
  IconCheckBg,
  Tabs,
} from 'gatorcomponents';
import ManageCard from './components/ManageCard';
import useLocale from '@/hooks/useLocale';
import FaqDefault from '@/components/FrequentlyQuestions/FaqDefault';
import { useDispatch, useSelector } from 'react-redux';
import { SUPPORT_DOMAINS_URL } from '@/config/urls/domainsUrls';
import { useResize } from '@/hooks/useResize';
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import {
  CHARLIE_CLOUDFLARE_FEATURES, CHARLIE_CLOUDFLARE_SECURITY_RULES, CHARLIE_CLOUDFLARE_SPEED_ANALYSIS, CHARLIE_CLOUDFLARE_VISITORS_ANALYSIS,
} from '@/config/GrowthBook/constants';
import { useHistory, useParams } from 'react-router';
import {
  ERROR_MODAL_STATUS, FEATURES, SUCCESS_MODAL_STATUS, TABS,
} from './ManageCloudFlareHandler.types';
import { cloudflareActions } from '@/redux/modules';
import CloudFlareGenericModal from '../CloudFlareGenericModal';
import { ChangePlanModal } from '../CloudFlareHandler/components/ChangePlanModal';
import { cloudflareAnalytics } from '@/analytics/cloudflare';
import { FeatureSpeed } from './components/FeatureSpeed';
import { ProgressBarBlock } from './components/ProgressBarBlock';
import { FeatureVisitors } from './components/FeatureVisitors';
import { Security } from './components/Security';
import { CloudflareConfirmModal } from '../CloudflareConfirmModal';

export const ManageCloudFlareHandler = () => {
  const [displayCleanCacheModal, setDisplayCleanCacheModal] = useState(false);
  const [displayErrorModal, setDisplayErrorModal] = useState(false);
  const [displaySuccessModal, setDisplaySuccessModal] = useState(false);
  const [errorModalStatus, setErrorModalStatus] = useState([ERROR_MODAL_STATUS.GENERIC]);
  const [successModalStatus, setSuccessModalStatus] = useState([SUCCESS_MODAL_STATUS.GENERIC]);
  const [changeDomainModal, setChangeDomainModal] = useState(false);
  const [blockingErrorModal, setBlockingErrorModal] = useState(false);
  const [deleteSuccessModal, setDeleteSuccessModal] = useState(false);
  const [currentDomain, setCurrentDomain] = useState(null);
  const [changePlanSelectedOption, setChangePlanSelectedOption] = useState(null);
  const withSpeedDetails = useFeatureIsOn(CHARLIE_CLOUDFLARE_SPEED_ANALYSIS);
  const withVisitorsDetails = useFeatureIsOn(CHARLIE_CLOUDFLARE_VISITORS_ANALYSIS);
  const withSecurityRules = useFeatureIsOn(CHARLIE_CLOUDFLARE_SECURITY_RULES);
  const [activeTab, setActiveTab] = useState(TABS.tools);
  const [deleteRuleConfirmModal, setDeleteRuleConfirmModal] = useState({
    show: false,
    buttonAction: null,
  });

  const history = useHistory();
  const dispatch = useDispatch();
  const { cloudflareInternalId } = useParams();

  const faq = useSelector(state => state.newFaq);
  const cloudflareState = useSelector(state => state.cloudflare);
  const activeDomainsList = cloudflareState.activeDomains.list;

  const { features: featuresState } = cloudflareState;
  const { status: togglesStatus } = featuresState;

  const purgeCache = togglesStatus && togglesStatus[FEATURES.PURGE_CACHE] && togglesStatus[FEATURES.PURGE_CACHE];
  const developerMode = togglesStatus && togglesStatus[FEATURES.DEV_MODE] && togglesStatus[FEATURES.DEV_MODE];
  const underAttackMode = togglesStatus && togglesStatus[FEATURES.UNDER_ATTACK_MODE] && togglesStatus[FEATURES.UNDER_ATTACK_MODE];
  const alwaysOnlineMode = togglesStatus && togglesStatus[FEATURES.ALWAYS_ONLINE] && togglesStatus[FEATURES.ALWAYS_ONLINE];

  const someFeatureIsLoading = (developerMode && developerMode.loading)
    || (underAttackMode && underAttackMode.loading)
    || (alwaysOnlineMode && alwaysOnlineMode.loading);

  const getCurrentDomain = useCallback(() => {
    const domainData = activeDomainsList.find(domain => domain.cloudflare_internal_id.toString() === cloudflareInternalId);
    setCurrentDomain(domainData);
  }, [activeDomainsList, cloudflareInternalId]);

  useEffect(() => {
    getCurrentDomain();
  }, [getCurrentDomain]);

  const cloudflareFeatures = useFeatureValue(CHARLIE_CLOUDFLARE_FEATURES);

  const { cloudflare: cloudflareLocale, routes: routesLocale } = useLocale();
  const manageCloudflareLocale = cloudflareLocale.manageCdnHandler;

  const windowRect = useResize();
  const { width } = windowRect;
  const isMobile = width < 600;

  const MAXIMUM_REGISTERED_RULES = 5;
  const rulesQty = cloudflareState.rules.list.length;
  const securityRulesLeft = MAXIMUM_REGISTERED_RULES - rulesQty;

  const updateFeatureStatus = (feature, featureStatus) => {
    const status = featureStatus === true ? 'off' : 'on';

    dispatch(cloudflareActions.features.update.request({
      cloudflareInternalId, feature, status,
    }));
  };

  const handleToggleDeveloperMode = () => {
    if (developerMode.status) cloudflareAnalytics.deactivateDevModeToggle();
    if (!developerMode.status) cloudflareAnalytics.activateDevModeToggle();

    updateFeatureStatus(FEATURES.DEV_MODE, developerMode.status);
  };

  const handleToggleUnderAttackMode = () => {
    if (underAttackMode.status) cloudflareAnalytics.deactivateUnderAttackToggle();
    if (!underAttackMode.status) cloudflareAnalytics.activateUnderAttackToggle();

    updateFeatureStatus(FEATURES.UNDER_ATTACK_MODE, underAttackMode.status);
  };

  const handleToggleAlwaysOnlineMode = () => {
    if (alwaysOnlineMode.status) cloudflareAnalytics.deactivateAlwaysOnlineToggle();
    if (!alwaysOnlineMode.status) cloudflareAnalytics.activateAlwaysOnlineToggle();

    updateFeatureStatus(FEATURES.ALWAYS_ONLINE, alwaysOnlineMode.status);
  };

  const handleClickChangeDomain = (domain) => {
    setChangeDomainModal(domain);
  };

  const handleCleanCache = () => {
    cloudflareAnalytics.cleanCacheCta();
    dispatch(cloudflareActions.purgeCache.request({ cloudflareInternalId }));
    setDisplayCleanCacheModal(true);
  };

  const cardsProps = {
    [FEATURES.PURGE_CACHE]: {
      ...manageCloudflareLocale.cdnCards[FEATURES.PURGE_CACHE],
      button: {
        ...manageCloudflareLocale.cdnCards[FEATURES.PURGE_CACHE].button,
        onClick: handleCleanCache,
      },
      icon: <IconTrashBg />,
      active: cloudflareFeatures && cloudflareFeatures.clean_cache, // mudar no GB para purge_cache e aplicar a key aqui
      blockedUntil: (purgeCache && purgeCache.blocked_until) || null,
      blockedTimeTooltip: manageCloudflareLocale.cdnCards[FEATURES.PURGE_CACHE].button.secondsRemainingTooltip,
    },
    [FEATURES.DEV_MODE]: {
      ...manageCloudflareLocale.cdnCards[FEATURES.DEV_MODE],
      toggle: {
        toggleId: 'developer-mode',
        onMark: handleToggleDeveloperMode,
        checked: (developerMode && developerMode.status) || false,
        loading: (developerMode && developerMode.loading) || false,
      },
      icon: <IconLive secondaryColor="pendingPure" />,
      active: cloudflareFeatures && cloudflareFeatures[FEATURES.DEV_MODE],
      blockedUntil: (developerMode && developerMode.blocked_until) || null,
      blockedTimeTooltip: manageCloudflareLocale.cdnCards[FEATURES.DEV_MODE].secondsRemainingTooltip,
    },
    [FEATURES.UNDER_ATTACK_MODE]: {
      ...manageCloudflareLocale.cdnCards[FEATURES.UNDER_ATTACK_MODE],
      toggle: {
        toggleId: 'under-attack-mode',
        onMark: handleToggleUnderAttackMode,
        checked: (underAttackMode && underAttackMode.status) || false,
        loading: (underAttackMode && underAttackMode.loading) || false,
      },
      icon: <IconSecurityCheck secondaryColor="activeLight" />,
      active: cloudflareFeatures && cloudflareFeatures[FEATURES.UNDER_ATTACK_MODE],
      blockedUntil: (underAttackMode && underAttackMode.blocked_until) || null,
      blockedTimeTooltip: manageCloudflareLocale.cdnCards[FEATURES.UNDER_ATTACK_MODE].secondsRemainingTooltip,
    },
    [FEATURES.ALWAYS_ONLINE]: {
      ...manageCloudflareLocale.cdnCards[FEATURES.ALWAYS_ONLINE],
      toggle: {
        toggleId: 'always-online-mode',
        onMark: handleToggleAlwaysOnlineMode,
        checked: (alwaysOnlineMode && alwaysOnlineMode.status) || false,
        loading: (alwaysOnlineMode && alwaysOnlineMode.loading) || false,
      },
      icon: <IconDoneChecklist secondaryColor="primaryBlue" />,
      active: cloudflareFeatures && cloudflareFeatures[FEATURES.ALWAYS_ONLINE],
      blockedUntil: (alwaysOnlineMode && alwaysOnlineMode.blocked_until) || null,
      blockedTimeTooltip: manageCloudflareLocale.cdnCards[FEATURES.ALWAYS_ONLINE].secondsRemainingTooltip,
    },
  };

  const modalErrorProps = {
    [ERROR_MODAL_STATUS.GENERIC]: {
      title: cloudflareLocale.genericErrorModal.title,
      message: cloudflareLocale.genericErrorModal.description,
      buttonLabel: cloudflareLocale.genericErrorModal.buttonLabel,
    },
    [ERROR_MODAL_STATUS.PURGE_CACHE]: {
      title: manageCloudflareLocale.cleanCacheModalError.title,
      message: manageCloudflareLocale.cleanCacheModalError.description,
      buttonLabel: manageCloudflareLocale.cleanCacheModalError.buttonLabel,
    },
    [ERROR_MODAL_STATUS.BLOCKING_ERROR]: {
      title: cloudflareLocale.blockingErrorModal.title,
      message: cloudflareLocale.blockingErrorModal.description,
      buttonLabel: cloudflareLocale.blockingErrorModal.buttonLabel,
    },
  };

  const modalSuccessProps = {
    [SUCCESS_MODAL_STATUS.ADD_NEW_RULE]: {
      title: manageCloudflareLocale.security.addRuleModalSuccess.title,
      message: manageCloudflareLocale.security.addRuleModalSuccess.description(securityRulesLeft),
      buttonLabel: manageCloudflareLocale.security.addRuleModalSuccess.buttonLabel,
    },

    [SUCCESS_MODAL_STATUS.GENERIC]: {
      title: cloudflareLocale.genericSuccessModal.title,
      message: cloudflareLocale.genericSuccessModal.description,
      buttonLabel: cloudflareLocale.genericSuccessModal.buttonLabel,
    },
    [SUCCESS_MODAL_STATUS.DELETE_RULE]: {
      title: cloudflareLocale.deleteRuleSuccessModal.title,
      message: cloudflareLocale.deleteRuleSuccessModal.description,
      buttonLabel: cloudflareLocale.deleteRuleSuccessModal.buttonLabel,
    },
  };

  const setAnalysisToolsTabAfterRequest = useCallback(() => {
    if (cloudflareState.analysis.finished) {
      setActiveTab(cloudflareState.analysis.finished);
    }
  }, [
    cloudflareState.analysis.finished,
  ]);

  useEffect(() => {
    setAnalysisToolsTabAfterRequest();
  }, [setAnalysisToolsTabAfterRequest]);

  const mainRequests = useCallback(() => {
    dispatch(cloudflareActions.domains.active.request());
    dispatch(cloudflareActions.features.request({ cloudflareInternalId }));
  }, [dispatch, cloudflareInternalId]);

  useEffect(() => {
    mainRequests();
  }, [mainRequests]);

  const handleCloseModal = () => {
    setDisplayErrorModal(false);
    setDisplaySuccessModal(false);
    setBlockingErrorModal(false);
    setDeleteSuccessModal(false);
    setDeleteRuleConfirmModal({ show: false });

    const loadingError = cloudflareState.activeDomains.status === 'failure' || cloudflareState.features.requestStatus === 'failure';

    if (errorModalStatus === ERROR_MODAL_STATUS.GENERIC && loadingError) {
      history.push(routesLocale.sites);
    }

    if (cloudflareState.rules.create.status !== null) {
      dispatch(cloudflareActions.security.resetAddRuleState());
    }

    if (cloudflareState.rules.delete.status !== null) {
      dispatch(cloudflareActions.security.resetDeleteRuleState());
    }

    if (cloudflareState.rules.edit.status !== null) {
      dispatch(cloudflareActions.security.resetEditRuleState());
    }
  };

  const manageErrors = useCallback(() => {
    setErrorModalStatus(ERROR_MODAL_STATUS.GENERIC);

    if (cloudflareState.activeDomains.loading || featuresState.loading) {
      setDisplayErrorModal(false);
    }
    if (featuresState.updateStatus === 'failure') {
      setDisplayErrorModal(true);
    }
    if (cloudflareState.activeDomains.status === 'failure') {
      setDisplayErrorModal(true);
    }
    if (cloudflareState.features.requestStatus === 'failure') {
      setDisplayErrorModal(true);
    }
    if (cloudflareState.rules.create.status === 'failure') {
      setErrorModalStatus(ERROR_MODAL_STATUS.BLOCKING_ERROR);
      setBlockingErrorModal(true);
    }
  }, [
    cloudflareState.activeDomains.status,
    cloudflareState.features.requestStatus,
    cloudflareState.activeDomains.loading,
    featuresState.updateStatus,
    featuresState.loading,
    cloudflareState.rules.create.status,
  ]);

  const manageSuccessModal = useCallback(() => {
    setSuccessModalStatus(SUCCESS_MODAL_STATUS.GENERIC);

    if (cloudflareState.rules.create.loading) {
      setDisplaySuccessModal(false);
    }

    if (cloudflareState.rules.create.status === 'success') {
      setSuccessModalStatus(SUCCESS_MODAL_STATUS.ADD_NEW_RULE);
      setDisplaySuccessModal(true);
    }

    if (cloudflareState.rules.delete.status === 'success') {
      setDeleteSuccessModal(SUCCESS_MODAL_STATUS.DELETE_RULE);
      setDisplaySuccessModal(true);
    }

    if (cloudflareState.rules.edit.loading) {
      setDisplaySuccessModal(false);
    }

    if (cloudflareState.rules.edit.status === 'success') {
      setDisplaySuccessModal(true);
    }
  }, [
    cloudflareState.rules.create.status,
    cloudflareState.rules.delete.status,
    cloudflareState.rules.edit.status,
    cloudflareState.rules.edit.loading,
    cloudflareState.rules.create.loading,
  ]);

  useEffect(() => {
    manageErrors();
    manageSuccessModal();
  }, [manageErrors, manageSuccessModal]);

  const getChangePlanModal = useCallback(() => {
    const handleSetPlan = (domain) => {
      setCurrentDomain(domain);
      history.push(`${routesLocale.products}${routesLocale.cloudflare}${routesLocale.domaindetail}/${domain.cloudflare_internal_id}`);
    };

    return {
      sites: activeDomainsList,
      onClose: () => setChangeDomainModal(false),
      handleSetPlan,
      changePlanSelectedOption,
      setChangePlanSelectedOption,
      currentPlan: currentDomain,
      backCta: cloudflareLocale.backCta,
      continueCta: cloudflareLocale.continueCta,
      title: cloudflareLocale.changePlanModal.title,
      searchPlaceholder: cloudflareLocale.changePlanModal.searchPlaceholder,
    };
  }, [
    activeDomainsList,
    changePlanSelectedOption,
    setChangePlanSelectedOption,
    currentDomain,
    cloudflareLocale,
    history,
    routesLocale,
  ]);

  const getDeleteRuleModalProps = useCallback(() => ({
    icon: <IconTrashBg size="xl" />,
    title: cloudflareLocale.deleteRuleConfirmModal.title,
    primaryButtonLabel: cloudflareLocale.deleteRuleConfirmModal.primaryButtonLabel,
    secondaryButtonLabel: cloudflareLocale.deleteRuleConfirmModal.secondaryButtonLabel,
    buttonAction: () => deleteRuleConfirmModal.buttonAction(),
    onClose: () => setDeleteRuleConfirmModal({ show: false, buttonAction: null }),
    loading: cloudflareState.rules.delete.loading,
  }), [
    cloudflareLocale,
    setDeleteRuleConfirmModal,
    deleteRuleConfirmModal,
    cloudflareState.rules.delete.loading,
  ]);

  const cleanupFunction = useCallback(() => {
    dispatch(cloudflareActions.domains.active.resetStatus());
    dispatch(cloudflareActions.features.resetRequestStatus());
    dispatch(cloudflareActions.features.update.resetStatus());
  }, [dispatch]);

  useEffect(() => () => {
    cleanupFunction();
  }, [cleanupFunction]);

  const genericErrorModalProps = {
    icon: <IconAtencion secondaryColor="activeMedium" size="xl" />,
    title: modalErrorProps[errorModalStatus].title,
    message: modalErrorProps[errorModalStatus].message,
    buttonLabel: modalErrorProps[errorModalStatus].buttonLabel,
    onClose: handleCloseModal,
    buttonAction: handleCloseModal,
  };

  const blockingErrorModalProps = {
    icon: <IconAtencion secondaryColor="problemLight" size="xl" />,
    title: modalErrorProps[errorModalStatus].title,
    message: modalErrorProps[errorModalStatus].message,
    buttonLabel: modalErrorProps[errorModalStatus].buttonLabel,
    onClose: handleCloseModal,
    buttonAction: handleCloseModal,
  };

  const genericSuccessModalProps = {
    icon: <IconCheckBg secondaryColor="activeMedium" size="xl" />,
    title: modalSuccessProps[successModalStatus].title,
    message: modalSuccessProps[successModalStatus].message,
    buttonLabel: modalSuccessProps[successModalStatus].buttonLabel,
    onClose: handleCloseModal,
    buttonAction: handleCloseModal,
  };

  const deleteRuleModalProps = {
    icon: <IconAtencion secondaryColor="activeMedium" size="xl" />,
    title: cloudflareLocale.deleteRuleSuccessModal.title,
    message: cloudflareLocale.deleteRuleSuccessModal.description,
    buttonLabel: cloudflareLocale.deleteRuleSuccessModal.buttonLabel,
    onClose: handleCloseModal,
    buttonAction: handleCloseModal,
  };

  const cleanCacheModalProps = useCallback(() => {
    const cleanCacheModalLocale = manageCloudflareLocale.cleanCacheModal;

    let title = cleanCacheModalLocale.loadingTitle;
    let message = '';
    let icon = null;

    if (cloudflareState.purgeCache.status === 'failure') {
      title = cleanCacheModalLocale.failureTitle;
      message = cleanCacheModalLocale.failureDescription;
      icon = <IconAtencion secondaryColor="problemLight" size="xl" />;
    }

    if (cloudflareState.purgeCache.status === 'success') {
      title = cleanCacheModalLocale.successTitle;
      message = cleanCacheModalLocale.successDescription;
      icon = <IconCheckBg secondaryColor="activeLight" size="xl" />;
    }

    return {
      onClose: () => setDisplayCleanCacheModal(false),
      buttonAction: () => setDisplayCleanCacheModal(false),
      testId: 'purge-cache-modal',
      loading: cloudflareState.purgeCache.loading,
      title,
      message,
      icon,
      buttonLabel: manageCloudflareLocale.cleanCacheModal.okButtonLabel,
    };
  }, [
    manageCloudflareLocale.cleanCacheModal,
    cloudflareState.purgeCache.loading,
    cloudflareState.purgeCache.status,
  ]);

  const somethingHasFailed = (!cloudflareState.activeDomains.loading && cloudflareState.activeDomains.status === 'failure')
  || (!cloudflareState.features.loading && cloudflareState.features.requestStatus === 'failure');

  const getTabsProps = useCallback(() => {
    const defaultOptions = {
      currentActiveTab: activeTab,
    };

    const options = [
      {
        label: manageCloudflareLocale.tabs.tools,
        id: 'tools',
        onClick: () => setActiveTab(TABS.tools),
      },
      {
        label: manageCloudflareLocale.tabs.speed,
        id: 'speed',
        onClick: () => {
          dispatch(cloudflareActions.analysis.getSpeedHistory({ cloudflareInternalId }));
          setActiveTab(TABS.speed);
        },
      },
      {
        label: manageCloudflareLocale.tabs.visitors,
        id: 'visitors',
        onClick: () => {
          dispatch(cloudflareActions.analysis.requestVisitorsAnalysis({ cloudflareInternalId }));
          setActiveTab(TABS.visitors);
        },
      },
      {
        label: manageCloudflareLocale.tabs.security,
        id: 'security',
        onClick: () => {
          setActiveTab(TABS.security);
          dispatch(cloudflareActions.security.getRules({ cloudflareInternalId }));
        },
      },
    ];

    if (!withSpeedDetails) {
      const index = options.findIndex(option => option.id === 'speed');
      if (index >= 0) {
        options.splice(index, 1);
      }
    }

    if (!withVisitorsDetails) {
      const index = options.findIndex(option => option.id === 'visitors');
      if (index >= 0) {
        options.splice(index, 1);
      }
    }

    if (!withSecurityRules) {
      const index = options.findIndex(option => option.id === 'security');
      if (index >= 0) {
        options.splice(index, 1);
      }
    }

    return {
      ...defaultOptions,
      options,
    };
  }, [
    activeTab,
    manageCloudflareLocale,
    dispatch,
    cloudflareInternalId,
    withVisitorsDetails,
    withSpeedDetails,
    withSecurityRules,
  ]);

  const resetRequestStatus = useCallback(() => {
    dispatch(cloudflareActions.analysis.statusReset());
  }, [dispatch]);

  const getFeatureSpeedProps = useCallback(() => {
    const speedHistoryData = cloudflareState.analysis.speedHistory.data;

    const defaultProps = {
      title: manageCloudflareLocale.speedAnalysis.title,
      listTitle: manageCloudflareLocale.speedAnalysis.listTitle,
      firstRow: manageCloudflareLocale.speedAnalysis.firstRow,
      secondRow: manageCloudflareLocale.speedAnalysis.secondRow,
      stillHave: manageCloudflareLocale.speedAnalysis.stillHave({ amount: speedHistoryData && speedHistoryData.tests_left }),
      dontHave: manageCloudflareLocale.speedAnalysis.dontHave,
      initTestLabel: manageCloudflareLocale.speedAnalysis.initTestLabel,
      reduTestLabel: manageCloudflareLocale.speedAnalysis.reduTestLabel,
      testDate: manageCloudflareLocale.speedAnalysis.testDate,
      loadingLastAnalysis: cloudflareState.analysis.speedHistory.loading,
      loadingHistory: cloudflareState.analysis.loadingHistory,
      history: speedHistoryData,
      leftAmount: speedHistoryData && speedHistoryData.tests_left,
      lastAnalysis: cloudflareState.analysis.lastAnalysis,
      resultTitle: manageCloudflareLocale.speedAnalysis.results.title,
      resultAnalysisTitle: manageCloudflareLocale.speedAnalysis.results.analysisTitle,
      resultItems: manageCloudflareLocale.speedAnalysis.results.items,
      showList: manageCloudflareLocale.speedAnalysis.showList,
      hideList: manageCloudflareLocale.speedAnalysis.hideList,
      failedTitle: manageCloudflareLocale.speedAnalysis.results.failedTitle,
      videoAbout: manageCloudflareLocale.speedAnalysis.results.videoAbout,
      failedDescription: manageCloudflareLocale.speedAnalysis.results.failedDescription(currentDomain.domain),
      historyListTitle: manageCloudflareLocale.speedAnalysis.historyList.title,
      dateLabel: manageCloudflareLocale.speedAnalysis.historyList.date,
      domainLabel: manageCloudflareLocale.speedAnalysis.historyList.domain,
      scoreLabel: manageCloudflareLocale.speedAnalysis.historyList.score,
      showDetailsButtonLabel: manageCloudflareLocale.speedAnalysis.historyList.showDetails,
      itemsPerPageLabel: manageCloudflareLocale.speedAnalysis.historyList.itemsPerPage,
      getHistoryError: {
        title: manageCloudflareLocale.speedAnalysis.getHistoryListError.title,
        description: manageCloudflareLocale.speedAnalysis.getHistoryListError.description,
      },
      getHistoryRequestStatus: cloudflareState.analysis.speedHistory.status,
      resetRequestStatus,
    };

    const getSpeedHistory = ({ perPage = 5, currentPage = 1, updateLastAnalysis = true }) => {
      dispatch(cloudflareActions.analysis.getSpeedHistory({
        cloudflareInternalId, perPage, currentPage, updateLastAnalysis,
      }));
    };

    const requestSpeedAnalysis = () => {
      setActiveTab(TABS.progressBar);
      dispatch(cloudflareActions.analysis.requestSpeedAnalysis({ cloudflareInternalId, clearLastAnalysisId: true }));
    };

    return {
      ...defaultProps,
      requestSpeedAnalysis,
      getSpeedHistory,
    };
  }, [
    cloudflareState,
    manageCloudflareLocale,
    cloudflareInternalId,
    dispatch,
    resetRequestStatus,
    currentDomain,
  ]);

  const resetVisitorsState = useCallback(() => {
    dispatch(cloudflareActions.analysis.resetVisitorsAnalysis());
  }, [dispatch]);

  const getVisitorsProps = useCallback(() => {
    const defaultProps = {
      visitsOnSiteTitle: manageCloudflareLocale.visitors.visitsOnSiteTitle,
      totalOfAccess: manageCloudflareLocale.visitors.totalOfAccess,
      maxOfVisitants: manageCloudflareLocale.visitors.maxOfVisitants,
      minOfVisitants: manageCloudflareLocale.visitors.minOfVisitants,
      perDay: manageCloudflareLocale.visitors.perDay,
      perHour: manageCloudflareLocale.visitors.perHour,
      selectOptions: manageCloudflareLocale.visitors.selectOptions,
      uniqueVisitants: manageCloudflareLocale.visitors.uniqueVisitants,
      startTimeEndTime: manageCloudflareLocale.visitors.startTimeEndTime,

      regionsOfAccessTitle: manageCloudflareLocale.visitors.regionsOfAccessTitle,
      countryTitle: manageCloudflareLocale.visitors.countryTitle,
      trafficTitle: manageCloudflareLocale.visitors.trafficTitle,
      perPageLabel: manageCloudflareLocale.visitors.perPageLabel,
      countryData: cloudflareState.analysis.visitors.requests,
      uniqueVisitorsData: cloudflareState.analysis.visitors.unique_visitors,
      requestStatus: cloudflareState.analysis.visitors.status,
      loading: cloudflareState.analysis.visitors.loading,
      noDataTitle: manageCloudflareLocale.visitors.noDataTitle,
      noDataDescription: manageCloudflareLocale.visitors.noDataDescription,
      errorStateTitle: manageCloudflareLocale.visitors.errorStateTitle,
      errorStateDescription: manageCloudflareLocale.visitors.errorStateDescription,
    };

    const requestVisitors = ({ days }) => {
      dispatch(cloudflareActions.analysis.requestVisitorsAnalysis({ cloudflareInternalId, days }));
    };

    return {
      ...defaultProps,
      requestVisitors,
      resetVisitorsState,
    };
  }, [
    cloudflareState,
    manageCloudflareLocale,
    cloudflareInternalId,
    resetVisitorsState,
    dispatch,
  ]);

  const getProgressBarProps = useCallback(() => ({
    texts: manageCloudflareLocale.progressBar.texts,
  }), [
    manageCloudflareLocale,
  ]);

  const resetGetRulesStatus = useCallback(() => {
    dispatch(cloudflareActions.security.resetGetRulesStatus());
  }, [dispatch]);

  const getSecurityProps = useCallback(() => {
    const { ruleId, enabled } = cloudflareState.rules.ruleData;
    const { rulesGroupId } = cloudflareState.rules;

    const onAddBlockRule = (ruleData) => {
      dispatch(cloudflareActions.security.addRuleRequest({ ruleData, cloudflareInternalId }));
    };

    const onDeleteBlockRule = (ruleId) => {
      const { rulesGroupId } = cloudflareState.rules;
      dispatch(cloudflareActions.security.deleteRuleRequest({ rulesGroupId, ruleId, cloudflareInternalId }));
    };

    const showDeleteRuleConfirmModal = (id) => {
      setDeleteRuleConfirmModal({
        show: true,
        buttonAction: () => onDeleteBlockRule(id),
      });
    };

    const setFormData = (formData) => {
      dispatch(cloudflareActions.security.setRuleFormData(formData));
    };

    const onEditRule = (ruleFormData) => {
      const { description, lockType, value } = ruleFormData;

      const ruleData = {
        description, lock_type: lockType, value, enabled,
      };

      dispatch(cloudflareActions.security.editRuleRequest({
        ruleData, rulesGroupId, ruleId, cloudflareInternalId,
      }));
    };

    const toggleRuleStatus = (rule) => {
      const { rulesGroupId } = cloudflareState.rules;

      const {
        description, lock_type: lockType, value, enabled, rule_id: ruleId,
      } = rule;

      const ruleData = {
        description,
        lock_type: lockType,
        value,
        enabled,
      };

      dispatch(cloudflareActions.security.toggleRuleStatus({
        ruleData, rulesGroupId, ruleId, cloudflareInternalId,
      }));
    };

    const actionOnSubmit = cloudflareState.rules.submitAction;
    const submitForm = actionOnSubmit === 'create' ? onAddBlockRule : onEditRule;
    const ruleToEdit = cloudflareState.rules.list.find(rule => rule.rule_id === ruleId);
    const editRuleBlockedUntil = ruleToEdit && ruleToEdit.blocked_until;
    const createRuleBlockedUntil = cloudflareState.rules.create.blockedUntil;

    const submitBlockedUntil = actionOnSubmit === 'create'
      ? createRuleBlockedUntil
      : editRuleBlockedUntil;

    const isSubmitLoading = actionOnSubmit === 'create'
      ? cloudflareState.rules.create.loading
      : ruleToEdit && ruleToEdit.loading;

    return ({
      locales: manageCloudflareLocale.security,
      actionOnSubmit,
      isSubmitLoading,
      ruleToEdit,
      rulesList: cloudflareState.rules.list,
      rulesListLoading: cloudflareState.rules.loading,
      ruleFormData: cloudflareState.rules.ruleData,
      submitBlockedUntil,
      submitForm,
      showDeleteRuleConfirmModal,
      toggleRuleStatus,
      setFormData,
      resetGetRulesStatus,
      rulesLeft: securityRulesLeft,
      getRulesListStatus: cloudflareState.rules.status,
    });
  }, [
    manageCloudflareLocale,
    cloudflareState,
    cloudflareInternalId,
    securityRulesLeft,
    dispatch,
    resetGetRulesStatus,
  ]);

  const mountFaqDefault = (
    <Styles.FaqRow>
      <FaqDefault
        questions={faq.cloudflare.questions}
        link={SUPPORT_DOMAINS_URL}
        loading={faq.loading}
      />
    </Styles.FaqRow>
  );

  if (cloudflareState.activeDomains.loading || featuresState.loading || !currentDomain || somethingHasFailed) {
    return (
      <Styles.Wrapper>
        {displayErrorModal && <CloudFlareGenericModal {...genericErrorModalProps} />}
        {displaySuccessModal && <CloudFlareGenericModal {...genericSuccessModalProps} />}

        <Styles.SkeletonItem>
          <Styles.SkeletonHeaderColumn>
            <Skeleton variant="textLine" width="232px" />
            <Skeleton variant="textLine" width="512px" />
            {isMobile && <Skeleton variant="textLine" width="200px" />}
          </Styles.SkeletonHeaderColumn>
        </Styles.SkeletonItem>

        {Array.from(Array(4).keys()).map(item => (
          <Styles.SkeletonItem key={`skeleton_${item}`}>
            <Skeleton variant="rectangle" width="216px" height="24px" />
            <Styles.SkeletonText>
              <Skeleton variant="textLine" width="660px" />
              <Skeleton variant="textLine" width="540px" />
            </Styles.SkeletonText>

            <Styles.SkeletonButton>
              <Skeleton variant="button" />
            </Styles.SkeletonButton>
          </Styles.SkeletonItem>
        ))}
      </Styles.Wrapper>
    );
  }

  const containerRules = {
    [TABS.tools]: (
      <>
        {Object.keys(cardsProps).map(key => cardsProps[key].active && (
          <ManageCard
            key={key}
            testId={key}
            cardTitle={cardsProps[key].cardTitle}
            cardDescription={cardsProps[key].cardDescription}
            tooltipLabel={cardsProps[key].tooltipLabel}
            toggle={cardsProps[key].toggle}
            button={cardsProps[key].button}
            icon={cardsProps[key].icon}
            readMoreButtonLabel={manageCloudflareLocale.readMoreButton}
            isMobile={isMobile}
            blockedUntil={cardsProps[key].blockedUntil}
            blockedTimeTooltip={cardsProps[key].blockedTimeTooltip}
            someFeatureIsLoading={someFeatureIsLoading}
          />
        ))}

        <Styles.FooterText>{manageCloudflareLocale.footerLabel}</Styles.FooterText>

        {mountFaqDefault}
      </>
    ),
    [TABS.speed]: (
      <>
        <FeatureSpeed {...getFeatureSpeedProps()} />
        {mountFaqDefault}
      </>
    ),
    [TABS.visitors]: <FeatureVisitors {...getVisitorsProps()} />,
    [TABS.progressBar]: <ProgressBarBlock {...getProgressBarProps()} />,
    [TABS.security]: <Security {...getSecurityProps()} />,
  };

  return (
    <Styles.Wrapper>
      {displayErrorModal && <CloudFlareGenericModal {...genericErrorModalProps} />}
      {displayCleanCacheModal && <CloudFlareGenericModal {...cleanCacheModalProps()} />}
      {changeDomainModal && <ChangePlanModal {...getChangePlanModal()} />}
      {displaySuccessModal && <CloudFlareGenericModal {...genericSuccessModalProps} />}
      {blockingErrorModal && <CloudFlareGenericModal {...blockingErrorModalProps} />}
      {deleteSuccessModal && <CloudFlareGenericModal {...deleteRuleModalProps} />}
      {deleteRuleConfirmModal.show && <CloudflareConfirmModal {...getDeleteRuleModalProps()} />}

      {(withSpeedDetails || withVisitorsDetails || withSecurityRules) && (
        <Styles.TabsContainer>
          <Tabs
            {...getTabsProps()}
          />
        </Styles.TabsContainer>
      )}

      <Styles.HeadContainer>
        <Styles.Text>
          {manageCloudflareLocale.hostingLabel(currentDomain.product_name)}
        </Styles.Text>

        <Styles.ChangeDomainWrapper>
          <Styles.PlanName>
            {manageCloudflareLocale.selectedDomainLabel}
          </Styles.PlanName>

          <Styles.DomainContainer>
            <Styles.DomainLabel data-testid="domain">
              {currentDomain.domain}
            </Styles.DomainLabel>
            <Button
              variant="text"
              testId="change-domain-button"
              label={manageCloudflareLocale.changeButtonLabel}
              onClick={handleClickChangeDomain}
            />
          </Styles.DomainContainer>
        </Styles.ChangeDomainWrapper>
      </Styles.HeadContainer>

      {containerRules[activeTab]}

    </Styles.Wrapper>
  );
};

export default ManageCloudFlareHandler;
